import { useLang, dict, buildWhatsAppUrl } from "../i18n";
import { Globe, MessageCircle, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const langs = [
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "nl", label: "NL" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "work", label: t.nav.work },
    { id: "artist", label: t.nav.artist },
    { id: "studios", label: t.nav.studios },
    { id: "process", label: t.nav.process },
    { id: "faq", label: t.nav.faq },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="font-display text-xl md:text-2xl tracking-tighter">
            6<span style={{ color: "hsl(var(--red-2))" }}>i</span>nq
          </span>
          <span className="hidden sm:inline text-xs uppercase tracking-[0.28em] text-white/50">
            Tattooz
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={`nav-${n.id}`}
              className="text-sm uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-switcher"
              className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 transition-colors text-xs uppercase tracking-[0.2em]"
              aria-label="Change language"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{lang.toUpperCase()}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[8rem] bg-neutral-950 border-white/15">
              {langs.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`uppercase tracking-[0.2em] text-xs cursor-pointer ${
                    lang === l.code ? "text-[hsl(var(--red-2))]" : "text-white/80"
                  }`}
                >
                  {l.label} — {dict[l.code].nav.book.split(" ").slice(-1)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            data-testid="header-book-btn"
            href={buildWhatsAppUrl(t.whatsapp_pretext)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex btn-wa text-xs"
          >
            <MessageCircle className="w-4 h-4" /> {t.nav.book}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 border border-white/15"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="container-x py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-base uppercase tracking-[0.2em] text-white/80 hover:text-white"
              >
                {n.label}
              </a>
            ))}
            <a
              data-testid="mobile-book-btn"
              href={buildWhatsAppUrl(t.whatsapp_pretext)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa mt-2"
            >
              <MessageCircle className="w-4 h-4" /> {t.nav.book}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
